import dayjs from "dayjs";
import { AD_LAYOUT_STUBS } from "@consts";
import { canonical } from "@utils";

const buildSpangramHints = (spangram) => {
	if (!spangram) {
		return [];
	}

	const numberOfVowels = spangram
		.split("")
		.filter((l) => /[aeiou]/i.test(l)).length;

	return [
		{
			indexLabel: "One",
			hint: `Today's “Spangram” has ${numberOfVowels} ${
				numberOfVowels === 1 ? "vowel" : "vowels"
			}`,
			visible: false,
		},
		{
			indexLabel: "Two",
			hint: `The length of Today’s NYT Strands “Spangram” is ${spangram.length} letters`,
			visible: false,
		},
		{
			indexLabel: "Three",
			hint: `Today’s “Spangram” begins with the letter ${spangram
				.substring(0, 1)
				.toUpperCase()}`,
			visible: false,
		},
	];
};

export default {
	name: "NYTStrands",
	data() {
		return {
			AD_LAYOUT_STUBS,
			answerVisible: false,
			spangramHints: null,
			todayPuzzle: null,
			pastPuzzles: null,
			// Must live in data() so Nuxt serialises it into the fetch payload —
			// without that the client reads undefined and the timezone
			// correction below never fires.
			serverDateParam: null,
		};
	},
	// fetch: content loading (SSR + client nav). Non-blocking on the client.
	async fetch() {
		if (process.server) {
			this.serverDateParam = this.resolveDate();
		}

		await this.loadData();
	},
	// Compare-first: only correct the date when the visitor's day genuinely
	// differs from the server's, so we don't flash the spinner over content
	// that was already right.
	mounted() {
		if (
			this.serverDateParam &&
			this.resolveDate() !== this.serverDateParam
		) {
			this.$fetch();
		}
	},
	head() {
		return {
			title: `NYT Strands Answers & Hints for Today (${
				this.currentDateString
			}) - ${this.getPuzzleIdDisplay(this.todayPuzzle?.id)}`,
			meta: [
				{
					name: "description",
					hid: "description",
					content:
						"Stuck on NYT Strands? Get daily hints, tips, and all the answers you need to untangle today’s puzzle. Including an archive with all the past games.",
				},
			],
			link: [canonical(this.$route)],
		};
	},
	computed: {
		currentDateString() {
			return dayjs().format("MMMM DD, YYYY");
		},
		pastPuzzlesData() {
			if (!this.pastPuzzles) {
				return [];
			}

			return Object.entries(this.pastPuzzles).map(
				([yearMonth, group]) => {
					const dummyDate = dayjs(`${yearMonth}-01`);
					const groupLabel = `NYT Strands ${dummyDate.format(
						"MMMM YYYY"
					)} Answers`;

					const entries = group.map(
						({ printDate, id, spangram, themeWords }) => {
							const dateLabel =
								dayjs(printDate).format("MMMM DD, YYYY");
							const entryLabel = `${dateLabel} #${id}`;

							return {
								printDate,
								entryLabel,
								spangram,
								themeWords,
							};
						}
					);

					return {
						yearMonth,
						groupLabel,
						entries,
					};
				}
			);
		},
	},
	methods: {
		getPuzzleIdDisplay(id) {
			return id ? `#${id}` : "";
		},
		resolveDate() {
			return dayjs().format("YYYY-MM-DD");
		},
		async loadData() {
			try {
				const { data } = await this.$axios.get(
					`${this.$config.API_SERVICE}/v4/nyt-strands`,
					{ params: { date: this.resolveDate() } }
				);
				const { todayPuzzle, pastPuzzles } = data || {};

				this.spangramHints = buildSpangramHints(
					todayPuzzle?.spangram || ""
				);

				if (!todayPuzzle || !pastPuzzles) {
					this.setError(500);
					return;
				}

				this.todayPuzzle = todayPuzzle;
				this.pastPuzzles = pastPuzzles;
			} catch (err) {
				this.setError(err?.response?.status || 500);
			}
		},
		setError(statusCode) {
			// layouts/error.vue reads the code off the store, so both the store
			// and Nuxt's own error handler need telling.
			this.$store.commit("error/setStatusCode", statusCode);
			this.$nuxt.error({ statusCode });
		},
	},
};
